import { NETWORK_ACCESS_OPTIONS, USER_LEVELS } from '../../constants.js';
import { IPV4_CIDR_REGEX, IPV6_CIDR_REGEX } from '../networkUtils.js';
import { DVR_ACCESS } from '../dvrAccess.js';
import API from '../../api.js';

const isValidNetworkEntry = (entry) =>
  entry.match(IPV4_CIDR_REGEX) ||
  entry.match(IPV6_CIDR_REGEX) ||
  (entry + '/32').match(IPV4_CIDR_REGEX) ||
  (entry + '/128').match(IPV6_CIDR_REGEX);
const NETWORK_KEYS = Object.keys(NETWORK_ACCESS_OPTIONS);

const toEpgDays = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Empty means the key is absent (inherit the system default). 0 is a real value.
const storedEpgDay = (value) => toEpgDays(value) ?? '';

// An empty field removes the key: omit it on create, send null on update so
// the serializer merge drops it.
const writeEpgDay = (customProps, key, value, existingUser) => {
  const days = toEpgDays(value);
  if (days !== null) {
    customProps[key] = days;
  } else if (existingUser) {
    customProps[key] = null;
  } else {
    delete customProps[key];
  }
};

export const createUser = (values) => {
  return API.createUser(values);
};

export const updateUser = (userId, values, isAdmin, authUser) => {
  return API.updateUser(
    userId,
    values,
    isAdmin ? false : authUser.id === userId
  );
};

export const generateApiKey = (payload) => {
  return API.generateApiKey(payload);
};

export const revokeApiKey = (payload) => {
  return API.revokeApiKey(payload);
};

export const userToFormValues = (user) => {
  const customProps = user.custom_properties || {};
  const networks = customProps.allowed_networks || {};

  return {
    username: user.username,
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email,
    user_level: `${user.user_level}`,
    stream_limit: user.stream_limit || 0,
    channel_profiles:
      user.channel_profiles.length > 0
        ? user.channel_profiles.map((id) => `${id}`)
        : ['0'],
    xc_password: customProps.xc_password || '',
    output_format: customProps.output_format || '',
    output_profile: customProps.output_profile
      ? `${customProps.output_profile}`
      : '',
    // null = unrestricted (key absent). Array (including []) = explicit allowlist.
    allowed_m3u_profile_ids: Object.prototype.hasOwnProperty.call(
      customProps,
      'allowed_m3u_profile_ids'
    )
      ? (Array.isArray(customProps.allowed_m3u_profile_ids)
          ? customProps.allowed_m3u_profile_ids
          : []
        ).map((id) => `${id}`)
      : null,
    hide_adult_content: customProps.hide_adult_content || false,
    catchup_enabled: customProps.catchup_enabled !== false,
    vod_movies_enabled: customProps.vod_movies_enabled !== false,
    vod_series_enabled: customProps.vod_series_enabled !== false,
    dvr_access:
      customProps.dvr_access === DVR_ACCESS.NONE ||
      customProps.dvr_access === DVR_ACCESS.VIEW ||
      customProps.dvr_access === DVR_ACCESS.MANAGE
        ? customProps.dvr_access
        : DVR_ACCESS.VIEW,
    epg_days: storedEpgDay(customProps.epg_days),
    epg_prev_days: storedEpgDay(customProps.epg_prev_days),
    allowed_ips: [
      ...new Set(
        NETWORK_KEYS.flatMap((key) =>
          networks[key] ? networks[key].split(',').filter(Boolean) : []
        )
      ),
    ],
  };
};

export const formValuesToPayload = (values, existingUser) => {
  const customProps = { ...(existingUser?.custom_properties || {}) };
  const payload = { ...values };

  customProps.xc_password = payload.xc_password || '';
  delete payload.xc_password;

  customProps.output_format = payload.output_format || null;
  delete payload.output_format;

  customProps.output_profile = payload.output_profile
    ? parseInt(payload.output_profile, 10)
    : null;
  delete payload.output_profile;

  // Tri-state: null deletes the key (ALL). [] keeps the key (NONE).
  // [ids] is an explicit allowlist. Omit the key on create so we never
  // persist null; on update send null so the serializer merge removes it.
  if (
    !Object.prototype.hasOwnProperty.call(payload, 'allowed_m3u_profile_ids') ||
    payload.allowed_m3u_profile_ids === null
  ) {
    if (existingUser) {
      customProps.allowed_m3u_profile_ids = null;
    } else {
      delete customProps.allowed_m3u_profile_ids;
    }
  } else {
    customProps.allowed_m3u_profile_ids = (
      Array.isArray(payload.allowed_m3u_profile_ids)
        ? payload.allowed_m3u_profile_ids
        : []
    ).map((id) => parseInt(id, 10));
  }
  delete payload.allowed_m3u_profile_ids;

  customProps.hide_adult_content = payload.hide_adult_content || false;
  delete payload.hide_adult_content;

  customProps.catchup_enabled = payload.catchup_enabled !== false;
  delete payload.catchup_enabled;

  customProps.vod_movies_enabled = payload.vod_movies_enabled !== false;
  delete payload.vod_movies_enabled;

  customProps.vod_series_enabled = payload.vod_series_enabled !== false;
  delete payload.vod_series_enabled;

  // DVR is a single access level for standard users and admins. Streamers
  // have no DVR surface (unlike catchup/VOD via XC), so force none.
  // Coerce with == so string form values ('0') match numeric USER_LEVELS.
  const isStreamer = payload.user_level == USER_LEVELS.STREAMER;
  if (isStreamer) {
    customProps.dvr_access = DVR_ACCESS.NONE;
  } else {
    const level = payload.dvr_access;
    customProps.dvr_access =
      level === DVR_ACCESS.NONE || level === DVR_ACCESS.MANAGE
        ? level
        : DVR_ACCESS.VIEW;
  }
  delete payload.dvr_access;
  // Drop any leftover dual-flag keys from earlier drafts of this feature.
  delete customProps.dvr_view_enabled;
  delete customProps.dvr_manage_enabled;

  writeEpgDay(customProps, 'epg_days', payload.epg_days, existingUser);
  delete payload.epg_days;

  writeEpgDay(customProps, 'epg_prev_days', payload.epg_prev_days, existingUser);
  delete payload.epg_prev_days;

  const joined = (payload.allowed_ips || []).join(',');
  delete payload.allowed_ips;
  const allowed_networks = {};
  if (joined) {
    NETWORK_KEYS.forEach((key) => {
      allowed_networks[key] = joined;
    });
  }
  customProps.allowed_networks = allowed_networks;

  payload.custom_properties = customProps;

  if (payload.channel_profiles?.includes('0')) {
    payload.channel_profiles = [];
  }

  return payload;
};

export const getFormInitialValues = () => {
  return {
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    user_level: '0',
    stream_limit: 0,
    password: '',
    xc_password: '',
    output_format: '',
    output_profile: '',
    allowed_m3u_profile_ids: null,
    channel_profiles: [],
    hide_adult_content: false,
    catchup_enabled: true,
    vod_movies_enabled: true,
    vod_series_enabled: true,
    dvr_access: DVR_ACCESS.VIEW,
    epg_days: '',
    epg_prev_days: '',
    allowed_ips: [],
  };
};

export const getFormValidators = (user) => {
  return (values) => ({
    username: !values.username
      ? 'Username is required'
      : !values.username.match(/^[A-Za-z0-9._@-]+$/)
        ? 'Username may only contain letters, numbers, periods (.), underscores (_), at signs (@), and hyphens (-)'
        : null,
    password:
      !user && !values.password && values.user_level != USER_LEVELS.STREAMER
        ? 'Password is required'
        : null,
    xc_password:
      values.xc_password && !values.xc_password.match(/^[A-Za-z0-9._@-]+$/)
        ? 'XC password may only contain letters, numbers, periods (.), underscores (_), at signs (@), and hyphens (-)'
        : null,
    allowed_ips: (values.allowed_ips || []).some((t) => !isValidNetworkEntry(t))
      ? 'Invalid IP address or CIDR range'
      : null,
  });
};
